import { ChartCard, AppIcon, Major } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, Tooltip, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './EarningsSection.agentContext';
import {
  getMajorEarningsRating,
  getChartPositioning,
  AVERAGE_MEDIAN_EARLY_CAREER_WAGE,
  AVERAGE_MEDIAN_MID_CAREER_WAGE,
} from '../majors.utils';

const STRINGS = appStrings.modules.majors.earnings;

// Constants
const Y_AXIS_VALUES = [100, 80, 60, 40, 20, 0] as const;
const CHART_HEIGHT = 240;
const LABEL_HEIGHT = 22;
const Y_AXIS_WIDTH = 40;
const Y_AXIS_MARGIN = 8;
const CHART_LEFT_OFFSET = Y_AXIS_WIDTH + Y_AXIS_MARGIN;
const BAR_WIDTH = 60;
const SPACING_BELOW_ZERO_LINE = 24;
const MAX_BAR_PERCENTAGE = 100;

type BarType = 'average' | 'major';

type LegendItemProps = Readonly<{
  type: BarType;
  label: string;
}>;

function LegendItem({ type, label }: LegendItemProps) {
  return (
    <Box sx={legendItemSx}>
      <Box sx={legendColorSx(type)} />
      <Typography variant="text-sm-regular" sx={legendTextSx}>
        {label}
      </Typography>
    </Box>
  );
}

type BarGroupProps = Readonly<{
  label: string;
  bars: readonly {
    type: BarType;
    percentage: number;
    tooltip: string;
  }[];
}>;

function BarGroup({ label, bars }: BarGroupProps) {
  return (
    <Box sx={barsWrapperSx}>
      <Box sx={barsContainerSx}>
        {bars.map((bar, index) => (
          <Tooltip key={index} title={bar.tooltip} placement="top" arrow>
            <Box sx={barSx(bar.type, bar.percentage)} />
          </Tooltip>
        ))}
      </Box>
      <Typography
        sx={typographyLabelSx}
        color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
        variant="text-xs-regular"
      >
        {label}
      </Typography>
    </Box>
  );
}

export function EarningsSection({ major }: { major: Major }) {
  useAgentContext(major);

  const { rating, iconColor, icon } = getMajorEarningsRating(major);

  const LEGEND_ITEMS: { type: BarType; label: string }[] = [
    { type: 'average', label: STRINGS.legend.average },
    { type: 'major', label: major.name },
  ];

  const CAREER_DATA = [
    {
      label: STRINGS.stages.earlyCareer,
      bars: [
        {
          type: 'average',
          percentage: Math.min(AVERAGE_MEDIAN_EARLY_CAREER_WAGE / 1000, 100),
          tooltip: `$${AVERAGE_MEDIAN_EARLY_CAREER_WAGE.toLocaleString()}`,
        },
        {
          type: 'major',
          percentage: Math.min(major.medianWageEarlyCareer / 1000, 100),
          tooltip: `$${major.medianWageEarlyCareer.toLocaleString()}`,
        },
      ],
    },
    {
      label: STRINGS.stages.midCareer,
      bars: [
        {
          type: 'average',
          percentage: Math.min(AVERAGE_MEDIAN_MID_CAREER_WAGE / 1000, 100),
          tooltip: `$${AVERAGE_MEDIAN_MID_CAREER_WAGE.toLocaleString()}`,
        },
        {
          type: 'major',
          percentage: Math.min(major.medianWageMidCareer / 1000, 100),
          tooltip: `$${major.medianWageMidCareer.toLocaleString()}`,
        },
      ],
    },
  ] as const;

  return (
    <Box sx={contentContainerSx}>
      <ChartCard
        title={STRINGS.chart.title}
        overrideSx={{ container: chartContainerOverrideSx }}
        chartVariant={{
          type: 'CUSTOM',
          children: (
            <Box sx={chartCustomContentSx}>
              <Box sx={ratingAndLegendContainerSx}>
                <Box sx={ratingContainerSx}>
                  <Typography
                    variant="display-sm-semibold"
                    color={({ backrs }) => backrs.colors['Gray/900']}
                  >
                    {rating}
                  </Typography>
                  <Box sx={thumbsUpBadgeSx}>
                    <AppIcon iconName={icon} diameter="12px" color={iconColor} />
                  </Box>
                </Box>

                <Box sx={legendContainerSx}>
                  {LEGEND_ITEMS.map((item) => (
                    <LegendItem key={item.label} type={item.type} label={item.label} />
                  ))}
                </Box>
              </Box>

              <Box sx={chartContainerSx}>
                <Box sx={gridLinesContainerSx}>
                  {Y_AXIS_VALUES.map((value) => (
                    <Box key={value} sx={gridLineSx(value)} />
                  ))}
                </Box>
                <Box sx={yAxisContainerSx}>
                  {Y_AXIS_VALUES.map((value) => (
                    <Box key={value} sx={yAxisLineSx}>
                      <Typography variant="text-xs-regular" sx={yAxisLabelSx}>
                        ${value}k
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={barsBigContainerSx}>
                  {CAREER_DATA.map((career) => (
                    <BarGroup key={career.label} label={career.label} bars={career.bars} />
                  ))}
                </Box>
              </Box>
            </Box>
          ),
        }}
      />
      <Box sx={wageContainerSx}>
        <Box>
          <Typography
            variant="text-md-semibold"
            color={({ backrs }) => backrs.colors['Colors/Foreground/fg-brand-secondary']}
          >
            {STRINGS.wages.earlyCareerLabel}
          </Typography>
          <Box sx={wageDisplaySx}>
            <AppIcon diameter="20px" iconName="CoinsStacked01" color="Colors/Text/text-primary" />
            <Typography variant="text-sm-medium">
              ${major.medianWageEarlyCareer.toLocaleString()}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="text-md-semibold"
            color={({ backrs }) => backrs.colors['Colors/Foreground/fg-brand-secondary']}
          >
            {STRINGS.wages.midCareerLabel}
          </Typography>
          <Box sx={wageDisplaySx}>
            <AppIcon diameter="20px" iconName="CoinsStacked02" color="Colors/Text/text-primary" />
            <Typography variant="text-sm-medium">
              ${major.medianWageMidCareer.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const contentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-4xl'],
  alignItems: 'flex-start',
});

const wageContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  flex: '0 0 fit-content',
});
const ratingAndLegendContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
  justifyContent: 'space-between',
});

const ratingContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const thumbsUpBadgeSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  borderRadius: backrs.radius['radius-md'],
  padding: backrs.spacing['spacing-md'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const legendColorSx =
  (type: 'average' | 'major'): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '8px',
    height: '8px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: type === 'average' ? backrs.colors['Brand/25'] : backrs.colors['Brand/500'],
    border: `1px solid ${backrs.colors['Beige/950']}`,
  });

const legendTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const chartContainerSx: SxProps<Theme> = {
  display: 'flex',
  position: 'relative',
  height: `${CHART_HEIGHT}px`,
};

const gridLinesContainerSx: SxProps<Theme> = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: 'none',
};

const gridLineSx =
  (value: number): SxProps<Theme> =>
  ({ backrs }) => {
    const index = Y_AXIS_VALUES.indexOf(value as (typeof Y_AXIS_VALUES)[number]);
    const totalValues = Y_AXIS_VALUES.length - 1;
    const { labelHeightPercent } = getChartPositioning(LABEL_HEIGHT, CHART_HEIGHT);

    const topPercent =
      index === 0
        ? labelHeightPercent
        : index === totalValues
          ? 100 - labelHeightPercent
          : (index / totalValues) * 100;

    return {
      position: 'absolute',
      left: `${CHART_LEFT_OFFSET}px`,
      right: 0,
      top: `${topPercent}%`,
      height: '1px',
      backgroundColor: backrs.colors['Gray/100'],
    };
  };

const yAxisContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: `${Y_AXIS_WIDTH}px`,
  marginRight: `${Y_AXIS_MARGIN}px`,
  position: 'relative',
  zIndex: 1,
};

const yAxisLineSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  height: `${LABEL_HEIGHT}px`,
};

const yAxisLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textAlign: 'right',
  width: `${Y_AXIS_WIDTH}px`,
});

const barsBigContainerSx: SxProps<Theme> = {
  display: 'flex',
  position: 'absolute',
  left: `${CHART_LEFT_OFFSET}px`,
  right: 0,
  top: 0,
  bottom: 0,
};

const barsWrapperSx: SxProps<Theme> = () => {
  return {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    textAlign: 'center',
    gap: 1,
    alignItems: 'center',
    position: 'relative',
    height: '100%',
  };
};

const barsContainerSx: SxProps<Theme> = ({ backrs }) => {
  const { zeroLinePositionPx, distanceFromBottom } = getChartPositioning(
    LABEL_HEIGHT,
    CHART_HEIGHT,
  );

  return {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: backrs.spacing['spacing-lg'],
    paddingLeft: backrs.spacing['spacing-3xl'],
    paddingRight: backrs.spacing['spacing-3xl'],
    position: 'absolute',
    bottom: `${distanceFromBottom}px`,
    left: 0,
    right: 0,
    height: `${zeroLinePositionPx}px`,
    zIndex: 1,
  };
};

const typographyLabelSx: SxProps<Theme> = () => {
  const { distanceFromBottom } = getChartPositioning(LABEL_HEIGHT, CHART_HEIGHT);
  const typographyBottom = distanceFromBottom - SPACING_BELOW_ZERO_LINE;

  return {
    position: 'absolute',
    bottom: `${typographyBottom}px`,
    left: 0,
    right: 0,
    textAlign: 'center',
    zIndex: 1,
  };
};

const barSx =
  (type: 'average' | 'major', percentage: number): SxProps<Theme> =>
  ({ backrs }) => ({
    width: `${BAR_WIDTH}px`,
    height: `${(percentage / MAX_BAR_PERCENTAGE) * 100}%`,
    backgroundColor: type === 'average' ? backrs.colors['Brand/25'] : backrs.colors['Brand/500'],
    borderRadius: backrs.radius['radius-sm'],
    borderBottomLeftRadius: backrs.radius['radius-none'],
    borderBottomRightRadius: backrs.radius['radius-none'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    minHeight: '20px',
  });

const legendContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const legendItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: backrs.spacing['spacing-md'],
  alignItems: 'center',
});

const wageDisplaySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const chartCustomContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

const chartContainerOverrideSx: SxProps<Theme> = {
  minHeight: '100%',
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: '510px',
};
